import { createStore } from 'vuex'

import * as types from './mutation-types'
import _ from 'lodash'

import currentTime from '@app/shared/vue/store/current-time'

import companies from './companies'
import columns from './columns'
import dropdown from '../../shared/vue/store/modules/dropdown'
import filters from './filters'
import reconciliationTexts from './reconciliation-texts'
import segments from './segments'
import users from './users'
import triggers from './triggers'
import submissions from './submissions'

import serialize from './mixins/serialize'

import { SfLog } from '@app/adapters/logger'
import api from '../api'

let store = null

function clearStore() {
  store = null
}

window.addEventListener('turbolinks:before-visit', clearStore)

export default function createInsightsStore(
  allCompaniesCount,
  liteInsights,
  recentSegments,
  remarkTags,
  firmWorkflows
) {
  if (store) {
    return store
  }

  store = createStore({
    modules: {
      companies,
      columns,
      dropdown,
      filters,
      reconciliationTexts,
      segments,
      users,
      triggers,
      currentTime,
      submissions,
    },

    state() {
      return {
        allCompaniesCount,
        ledgerSelectDate: null,
        periodSelection: null,
        lite: liteInsights,
        isPremiumInfoModalOpen: false,
        recentSegments: recentSegments,
        periodSelectionOpen: false,
        remarkTags: remarkTags,
        firmWorkflows: firmWorkflows,
      }
    },

    mutations: {
      [types.SET_LEDGER_SELECT_DATE](state, value) {
        // make sure empty/undefined date will be stored as null to make it consistent
        state.ledgerSelectDate = !value ? null : value
      },
      [types.SET_PERIOD_SELECTION](state, value) {
        state.periodSelection = value
      },
      [types.TOGGLE_PREMIUM_INFO_MODAL](state) {
        state.isPremiumInfoModalOpen = !state.isPremiumInfoModalOpen
      },
      [types.ADD_RECENT_SEGMENT](state, segmentId) {
        state.recentSegments.unshift(segmentId)
      },
      [types.SET_PERIOD_SELECTION_OPEN](state, value) {
        state.periodSelectionOpen = value
      },
    },

    actions: {
      queuePeriodSelectionOpen({ commit }) {
        commit(types.SET_PERIOD_SELECTION_OPEN, true)
      },
      resetPeriodSelectionOpen({ commit }) {
        commit(types.SET_PERIOD_SELECTION_OPEN, false)
      },
      setLedgerSelectDate({ commit }, date) {
        commit(types.SET_LEDGER_SELECT_DATE, date)
      },

      setPeriodSelection({ commit }, strategy) {
        commit(types.SET_PERIOD_SELECTION, strategy)
      },

      stateToUrlParams({ getters, state }, route) {
        // Here we choose which parts of the state are important to persist between refresh, or back/forward's that cause a Vue component reinitialisation
        // This is also responsible for serialization the data to the url. Actual url encoding is done by the Vue router
        const params = _.pick(route.query, [
          'ms_id',
          'ms_name',
          'debug',
          'openFilterModal',
        ]) // ms_* are from a PartnerSegment

        if (state.segment) {
          params.segment_id = state.segment.id
        }

        if (!state.segment || getters.isSegmentModified) {
          params.filters = JSON.stringify(
            serialize.filterSettings(
              state.filters.activeFilters,
              state.filters.allFiltersByType
            )
          )
          if (state.ledgerSelectDate) {
            params.period_date = state.ledgerSelectDate
          }
          if (state.periodSelection) {
            params.period_selection = state.periodSelection
          }
          if (
            state.companies.pendingCompaniesRequest &&
            getters.getActiveFilters.length
          ) {
            params.requestRef = state.companies.pendingCompaniesRequest.ref
          }
        }
        return params
      },

      togglePremiumModal({ commit }) {
        commit(types.TOGGLE_PREMIUM_INFO_MODAL)
      },

      loadStateFromUrlQueryParams(
        { commit, dispatch, state, getters },
        params
      ) {
        // This is the deserialization step of +stateToUrlParams()+
        // 1: Fetch segment from the API and set it's data
        if (params.segment_id) {
          dispatch('loadSegment', {
            id: params.segment_id,
            partner_segment_id: params.partner_segment_id,
          })
        }
        // 2: set period_selection and period_date
        if (params.period_date) {
          commit(types.SET_LEDGER_SELECT_DATE, params.period_date)
        }
        if (params.period_selection) {
          commit(types.SET_PERIOD_SELECTION, params.period_selection)
        }

        // 3: load filters state
        if (params.filters) {
          const filters = JSON.parse(params.filters)
          filters.forEach((filter) => {
            const filterType = state.filters.allFilters.find(
              (typeConfig) => typeConfig.type === filter.type
            )
            filter.settings = filterType.deserializeSettings(filter.params)
            filter.filterType = filter.type
            filter.columnName = filter.column_name
            dispatch('addOrUpdateFilter', filter)
          })
        }

        if (params.openFilterModal) {
          const filter = getters.getActiveFilters.find(
            (f) => f.uuid === params.openFilterModal
          )

          if (filter) {
            const fullFilter = {
              ...filter,
              ...state.filters.allFiltersByType[filter.type],
            }
            dispatch('editFilter', fullFilter)
          }
        }
      },

      async handleFilterOrPeriodChange({ dispatch }, route) {
        await dispatch('cancelCurrentSegmentlessRun', route)
        const existingRequestRef = route.query.requestRef

        try {
          await dispatch('fetchFilteredCompanies', existingRequestRef)
        } catch (e) {
          SfLog.warn(
            'Error requesting Insights companies on filter/period change',
            {
              extra: {
                firmId: window.Silverfin.currentFirmId,
                error: e,
              },
            }
          )
        }

        return dispatch('storeDataInUrl', route)
      },

      async cancelCurrentSegmentlessRun({ getters }, route) {
        const { segment_id: currentSegmentId } = route.query
        if (
          !currentSegmentId &&
          getters.pendingCompaniesRequest &&
          getters.pendingCompaniesRequest.ref &&
          !getters.allCompaniesProcessed
        ) {
          await api.cancelPendingRequest({
            pending_request_ref: getters.pendingCompaniesRequest.ref,
          })
        }
      },

      async storeDataInUrl({ dispatch, state, getters }, route) {
        try {
          if (state.segments.segment && !getters.isSegmentModified) {
            const {
              segment_id: currentSegmentId,
              partner_segment_id: currentPartnerSegmentId,
            } = route.query

            const {
              id: nextSegmentId,
              partner_segment_id: nextPartnerSegmentId,
            } = state.segments.segment

            const differentSegmentId =
              nextSegmentId?.toString() !== currentSegmentId?.toString()

            const differentPartnerSegmentId =
              nextPartnerSegmentId?.toString() !==
              currentPartnerSegmentId?.toString()

            if (differentSegmentId || differentPartnerSegmentId) {
              return {
                query: {
                  segment_id: state.segments.segment.id,
                  partner_segment_id: state.segments.segment.partner_segment_id,
                },
              }
            }
          } else {
            const params = await dispatch('stateToUrlParams', route)
            return { query: params }
          }
        } catch (e) {
          SfLog.warn('Failed to store data in URL', {
            extra: {
              error: e,
            },
          })
        }
      },
    },
  })

  return store
}
